import { fetchIssueForAi, createIssue, linkIssues, fetchIssue, fetchUserByName, JIRA_FIELDS } from './jira-api.js';
import { callCopilot, getStoredCopilotToken, storeCopilotToken, clearCopilotToken } from './copilot-api.js';

const SYSTEM_PROMPT = `You are a Senior QA Engineer.

Transform my raw bug notes into a professional Jira defect.

Instructions:
- Identify the affected flow / feature area only if it is explicitly clear from the raw notes or provided context
- If the affected flow is not clear, write: "Affected flow: unclear"
- Do not guess
- If implementation steps are provided, treat them as the authoritative source for what the feature is supposed to do
- If there is a conflict between the ticket description and the implementation steps, the implementation steps take priority
- Do not assume behavior, root cause, or environment unless explicitly stated

Requirements:
- Create a concise but descriptive Summary
- Identify the Affected flow
- Generate Preconditions only if they are explicitly stated or are necessary assumptions for reproduction
- Generate clear Steps to Reproduce
- Generate Expected Result
- Generate Actual Result
- Suggest severity and priority
- Keep wording professional, concise, and human-like
- Do NOT invent details not present in the input
- If information is missing, mention assumptions explicitly
- If the notes are too vague, preserve uncertainty instead of filling gaps

Output format:
Summary:
Steps to Reproduce:
Expected Result:
Actual Result:
Suggested Priority:`;

const JIRA_BROWSE_BASE = 'https://over-haul.atlassian.net/browse';

function parseSummary(text) {
  const match = text.match(/^Summary:\s*(.+)/im);
  return match ? match[1].trim() : '';
}

const SKIP_LINE = /^(Summary|Suggested Priority)\s*:/i;
const STANDALONE_HEADER = /^([A-Za-z][^:\n]{1,50}):\s*$/;
const INLINE_HEADER = /^([A-Za-z][^:\n]{1,50}):\s+(.+)/;

function aiOutputToAdf(text) {
  const content = [];
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trimEnd();
    if (SKIP_LINE.test(line)) continue;
    if (!line.trim()) continue;
    const standalone = line.match(STANDALONE_HEADER);
    if (standalone) {
      content.push({ type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: standalone[1].trim() }] });
      continue;
    }
    const inline = line.match(INLINE_HEADER);
    if (inline) {
      content.push({ type: 'heading', attrs: { level: 3 }, content: [{ type: 'text', text: inline[1].trim() }] });
      content.push({ type: 'paragraph', content: [{ type: 'text', text: inline[2].trim() }] });
      continue;
    }
    content.push({ type: 'paragraph', content: [{ type: 'text', text: line }] });
  }
  return { type: 'doc', version: 1, content };
}

function parsePriority(text) {
  const match = text.match(/^Suggested Priority:\s*(.+)/im);
  if (!match) return 'Medium';
  const raw = match[1].trim().toLowerCase();
  if (raw.includes('highest') || raw.includes('critical')) return 'Highest';
  if (raw.includes('high')) return 'High';
  if (raw.includes('lowest')) return 'Lowest';
  if (raw.includes('low')) return 'Low';
  return 'Medium';
}

const FRONTEND_DEVELOPERS_SET = new Set([
  'Anton Zinchenko', 'Dmytro Siasko', 'Maksym Ryzhykov', 'Maksym Yavir',
  'Olena Shchadylo', 'Olha Slisarchuk', 'Orest Saturskyy', 'Rita Aloshkina', 'Vadym Tsummer',
]);

const BACKEND_DEVELOPERS_SET = new Set([
  'Anna Mashtalir', 'Anton Chupak', 'Denys Khomiuk', 'Maksym Stankovskyi',
  'Marian Rebeha', 'Mykhailo Dovhanych', 'Oleh Chupa', 'Orest Kosyk', 'Orest Ostashuk',
  'Rafael Gibim', 'Serhii Bodnaruk', 'Serhii Svyrydov', 'Vasyl Dalekorei',
  'Viacheslav Mozulevskyi', 'Viktor Sych', 'Volodymyr Shafran', 'Yevhenii Moldavchuk', 'Yura Brehey',
]);

function resolveAssigneePromise(devs) {
  return devs[0]?.value ? fetchUserByName(devs[0].value) : Promise.resolve(null);
}

function buildTeamEntry(assignee, devs, baseLabels, stripLabel, addLabel) {
  return {
    assigneeId: assignee?.accountId ?? null,
    assignedTo: devs.map(t => ({ value: t.value })),
    labels: [...new Set([...baseLabels.filter(l => l !== stripLabel), addLabel])],
  };
}

function resolveTeamFields(team, fe, be) {
  if (team === 'fe') return { assigneeId: fe?.assigneeId ?? null, assignedTo: fe?.assignedTo ?? [], labels: fe?.labels ?? [] };
  if (team === 'be') return { assigneeId: be?.assigneeId ?? null, assignedTo: be?.assignedTo ?? [], labels: be?.labels ?? [] };
  return {
    assigneeId: fe?.assigneeId ?? be?.assigneeId ?? null,
    assignedTo: [...(fe?.assignedTo ?? []), ...(be?.assignedTo ?? [])],
    labels: [...new Set([...(fe?.labels ?? []), ...(be?.labels ?? [])])],
  };
}

function fallbackTeamLabels(team) {
  if (team === 'fe') return ['FRONTEND'];
  if (team === 'be') return ['BACKEND'];
  return ['FRONTEND', 'BACKEND'];
}

function buildImplStepsParts(ticketData) {
  const parts = [];
  if (!ticketData.feImplSteps && !ticketData.beImplSteps) return parts;
  parts.push('\n--- Implementation Steps (authoritative; override description on conflict) ---');
  if (ticketData.feImplSteps) {
    parts.push('Frontend Implementation Steps:');
    parts.push(ticketData.feImplSteps);
  }
  if (ticketData.beImplSteps) {
    parts.push('Backend Implementation Steps:');
    parts.push(ticketData.beImplSteps);
  }
  parts.push('--- End of Implementation Steps ---');
  return parts;
}

function buildUserMessage(ticketData, rawNotes) {
  const parts = ['Context:'];

  if (ticketData) {
    parts.push(`Ticket: ${ticketData.key}`);
    parts.push(`Summary: ${ticketData.summary}`);
    if (ticketData.labels.length) parts.push(`Labels: ${ticketData.labels.join(', ')}`);
    if (ticketData.links.length) {
      parts.push('Linked Issues:');
      ticketData.links.forEach(l => parts.push(`  - ${l}`));
    }
    if (ticketData.descriptionText) {
      parts.push('\nDescription:');
      parts.push(ticketData.descriptionText.slice(0, 2000));
    }
    parts.push(...buildImplStepsParts(ticketData));
  }

  parts.push('\nRaw Notes:');
  parts.push(rawNotes.trim() || '(none)');

  return parts.join('\n');
}

async function buildTicketFields(parentKey, team, outputText) {
  const parentIssue = await fetchIssue(parentKey);
  const pf = parentIssue.fields;
  const sprints = pf[JIRA_FIELDS.SPRINT];
  const sprint = Array.isArray(sprints)
    ? (sprints.find(s => s.state === 'active') ?? sprints.at(-1))
    : null;
  const baseLabels = (Array.isArray(pf.labels) ? pf.labels : []).filter(l => l !== 'Testing');
  const epicKey = pf.parent?.key ?? null;
  const teamOptions = Array.isArray(pf[JIRA_FIELDS.TEAM])
    ? pf[JIRA_FIELDS.TEAM].map(t => ({ value: t.value }))
    : null;
  const rawAssignedTo = Array.isArray(pf[JIRA_FIELDS.ASSIGNED_TO]) ? pf[JIRA_FIELDS.ASSIGNED_TO] : [];
  const filteredFeDevs = rawAssignedTo.filter(t => FRONTEND_DEVELOPERS_SET.has(t.value));
  const filteredBeDevs = rawAssignedTo.filter(t => BACKEND_DEVELOPERS_SET.has(t.value));
  const [feAssignee, beAssignee] = await Promise.all([
    resolveAssigneePromise(filteredFeDevs),
    resolveAssigneePromise(filteredBeDevs),
  ]);
  const fe = buildTeamEntry(feAssignee, filteredFeDevs, baseLabels, 'BACKEND', 'FRONTEND');
  const be = buildTeamEntry(beAssignee, filteredBeDevs, baseLabels, 'FRONTEND', 'BACKEND');
  const { assigneeId, assignedTo, labels } = resolveTeamFields(team, fe, be);
  const effectiveLabels = labels.length > 0 ? labels : fallbackTeamLabels(team);
  const fields = {
    summary: parseSummary(outputText),
    priority: { name: parsePriority(outputText) },
    [JIRA_FIELDS.DESCRIPTION_BUG]: aiOutputToAdf(outputText),
    labels: [...new Set([...effectiveLabels, 'DEVELOPMENT'])],
  };
  if (sprint?.id) fields[JIRA_FIELDS.SPRINT] = sprint.id;
  if (epicKey) fields.parent = { key: epicKey };
  if (assigneeId) fields.assignee = { accountId: assigneeId };
  if (teamOptions?.length) fields[JIRA_FIELDS.TEAM] = teamOptions;
  if (assignedTo.length) fields[JIRA_FIELDS.ASSIGNED_TO] = assignedTo;
  return fields;
}

const TAB_STYLES = `
  #ai-panel {
    display: flex; flex-direction: column; gap: 14px;
    padding: 16px 18px; overflow-y: auto; flex: 1; min-height: 0;
    background: #fafafa;
  }
  #ai-token-screen { display: flex; flex-direction: column; gap: 12px; }
  #ai-token-screen.hidden { display: none; }
  #ai-main { display: flex; flex-direction: column; gap: 14px; }
  #ai-main.hidden { display: none; }
  .ai-field { display: flex; flex-direction: column; gap: 4px; }
  .ai-label { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; }
  .ai-input {
    border: 1px solid #d1d5db; border-radius: 6px; padding: 7px 10px;
    font-size: 13px; outline: none; width: 100%; background: #fff; font-family: inherit;
  }
  .ai-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.15); }
  textarea.ai-input { resize: vertical; min-height: 80px; }
  .ai-hint { font-size: 12px; color: #6b7280; margin-top: 3px; }
  .ai-hint.error { color: #dc2626; }
  .ai-hint.loading { color: #6366f1; }
  .ai-row { display: flex; align-items: center; gap: 8px; }
  .ai-btn {
    background: #1e1b4b; color: #fff; border: none; border-radius: 6px;
    padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; flex-shrink: 0;
  }
  .ai-btn:hover:not(:disabled) { background: #312e81; }
  .ai-btn:disabled { opacity: .45; cursor: not-allowed; }
  .ai-btn.secondary {
    background: #fff; color: #1e1b4b; border: 1px solid #d1d5db;
  }
  .ai-btn.secondary:hover:not(:disabled) { background: #f3f4f6; }
  .ai-link-btn { background: none; border: none; color: #6366f1; font-size: 11px; cursor: pointer; padding: 0; text-decoration: underline; }
  #ai-context-preview {
    border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px 12px;
    font-size: 11px; color: #374151; background: #f9fafb; line-height: 1.6;
    display: none;
  }
  #ai-context-preview.visible { display: block; }
  #ai-context-preview b { color: #1e1b4b; }
  #ai-output-wrap { display: none; flex-direction: column; gap: 6px; }
  #ai-output-wrap.visible { display: flex; }
  #ai-output {
    border: 1px solid #d1d5db; border-radius: 6px; padding: 12px 14px;
    font-size: 12px; color: #111827; background: #fff; white-space: pre-wrap;
    line-height: 1.7; max-height: 340px; overflow-y: auto; font-family: inherit;
  }
  .ai-toggle-group { display: flex; border-radius: 6px; overflow: hidden; border: 1px solid #d1d5db; }
  .ai-toggle { flex: 1; background: #fff; border: none; border-right: 1px solid #d1d5db; padding: 6px 8px; font-size: 12px; font-weight: 500; cursor: pointer; color: #374151; font-family: inherit; }
  .ai-toggle:last-child { border-right: none; }
  .ai-toggle.active { background: #1e1b4b; color: #fff; }
  .ai-toggle:hover:not(.active) { background: #f3f4f6; }
  #ai-create-wrap { display: none; flex-direction: column; gap: 10px; border-top: 1px solid #e5e7eb; padding-top: 12px; }
  #ai-create-wrap.visible { display: flex; }
`;

export function initAiTab({ root, getParentKey }) {
  const styleEl = document.createElement('style');
  styleEl.textContent = TAB_STYLES;
  root.appendChild(styleEl);

  const panel = document.createElement('div');
  panel.id = 'ai-panel';
  panel.style.display = 'none';
  panel.innerHTML = `
    <div id="ai-token-screen">
      <div class="ai-field">
        <span class="ai-label">GitHub Token</span>
        <input class="ai-input" id="ai-token-input" type="text" placeholder="ghp_..." autocomplete="off" spellcheck="false" />
        <div class="ai-hint">
          Stored locally in this browser only.<br><br>
          <b>How to generate:</b><br>
          1. Go to <a href="https://github.com/settings/tokens" target="_blank" rel="noopener" style="color:#6366f1">github.com/settings/tokens</a><br>
          2. Click <b>Generate new token (classic)</b><br>
          3. Under <i>Select scopes</i>, check <b>copilot</b><br>
          4. Click <b>Generate token</b> and paste it here
        </div>
      </div>
      <div class="ai-row">
        <button class="ai-btn" id="ai-save-token">Save &amp; Continue</button>
        <span class="ai-hint" id="ai-token-status"></span>
      </div>
    </div>
    <div id="ai-main" class="hidden">
      <div class="ai-field">
        <span class="ai-label">Jira Ticket (context)</span>
        <input class="ai-input" id="ai-ticket-input" type="text" placeholder="OK-1234" autocomplete="off" spellcheck="false" />
      </div>
      <div id="ai-context-preview"></div>
      <div class="ai-field">
        <span class="ai-label">Raw Notes *</span>
        <textarea class="ai-input" id="ai-notes" placeholder="Describe what went wrong in plain words…" autocomplete="off"></textarea>
      </div>
      <div class="ai-row">
        <button class="ai-btn" id="ai-generate">✨ Generate</button>
        <span class="ai-hint" id="ai-generate-status" style="flex:1"></span>
        <button class="ai-link-btn" id="ai-change-token" style="margin-left:auto">Change token</button>
      </div>
      <div id="ai-output-wrap">
        <div class="ai-row">
          <span class="ai-label" style="flex:1">Generated Report</span>
          <button class="ai-btn secondary" id="ai-copy-output" style="padding:4px 10px;font-size:11px">Copy</button>
        </div>
        <div id="ai-output"></div>
      </div>
      <div id="ai-create-wrap">
        <span class="ai-label">Create Bug Ticket</span>
        <div class="ai-toggle-group" id="ai-team-toggle">
          <button class="ai-toggle active" data-value="fe">Frontend</button>
          <button class="ai-toggle" data-value="be">Backend</button>
          <button class="ai-toggle" data-value="both">Both</button>
        </div>
        <div class="ai-row">
          <button class="ai-btn" id="ai-create-ticket">Create Ticket</button>
          <span class="ai-hint" id="ai-create-status" style="flex:1"></span>
        </div>
      </div>
    </div>
  `;
  root.getElementById('body-row').after(panel);

  const tokenScreen = root.getElementById('ai-token-screen');
  const mainPanel = root.getElementById('ai-main');
  const ticketInput = root.getElementById('ai-ticket-input');
  const contextPreview = root.getElementById('ai-context-preview');
  const generateBtn = root.getElementById('ai-generate');
  const generateStatus = root.getElementById('ai-generate-status');
  const outputWrap = root.getElementById('ai-output-wrap');
  const outputEl = root.getElementById('ai-output');
  const createWrap = root.getElementById('ai-create-wrap');
  const createStatus = root.getElementById('ai-create-status');

  let loadedTicket = null;
  let selectedTeam = 'fe';

  root.getElementById('ai-team-toggle').querySelectorAll('.ai-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      selectedTeam = btn.dataset.value;
      root.getElementById('ai-team-toggle').querySelectorAll('.ai-toggle').forEach(b => b.classList.toggle('active', b === btn));
    });
  });

  function showTokenScreen() {
    tokenScreen.classList.remove('hidden');
    mainPanel.classList.add('hidden');
  }
  function showMain() {
    tokenScreen.classList.add('hidden');
    mainPanel.classList.remove('hidden');
  }

  if (getStoredCopilotToken()) showMain(); else showTokenScreen();

  root.getElementById('ai-save-token').addEventListener('click', () => {
    const val = root.getElementById('ai-token-input').value.trim();
    const statusEl = root.getElementById('ai-token-status');
    if (!val) {
      statusEl.textContent = 'Please enter a token.';
      statusEl.className = 'ai-hint error';
      return;
    }
    storeCopilotToken(val);
    root.getElementById('ai-token-input').value = '';
    statusEl.textContent = '';
    showMain();
  });

  root.getElementById('ai-change-token').addEventListener('click', () => {
    clearCopilotToken();
    loadedTicket = null;
    contextPreview.classList.remove('visible');
    createWrap.classList.remove('visible');
    showTokenScreen();
  });

  function renderContextPreview() {
    if (!loadedTicket) return;
    const t = loadedTicket;
    contextPreview.textContent = '';
    const div = document.createElement('div');
    const bold = document.createElement('b');
    bold.textContent = `${t.key}:`;
    div.appendChild(bold);
    div.appendChild(document.createTextNode(` ${t.summary}`));
    contextPreview.appendChild(div);
    contextPreview.classList.add('visible');
  }

  root.getElementById('ai-generate').addEventListener('click', async () => {
    const notes = root.getElementById('ai-notes').value;
    const ticketKey = ticketInput.value.trim().toUpperCase();
    if (!ticketKey) {
      generateStatus.textContent = 'Jira ticket key is required.';
      generateStatus.className = 'ai-hint error';
      return;
    }
    if (!notes.trim()) {
      generateStatus.textContent = 'Raw notes are required.';
      generateStatus.className = 'ai-hint error';
      return;
    }
    generateBtn.disabled = true;
    outputWrap.classList.remove('visible');
    createWrap.classList.remove('visible');
    contextPreview.classList.remove('visible');
    try {
      if (loadedTicket?.key !== ticketKey) {
        generateStatus.textContent = 'Fetching ticket…';
        generateStatus.className = 'ai-hint loading';
        loadedTicket = await fetchIssueForAi(ticketKey);
        renderContextPreview();
      }
      generateStatus.textContent = 'Generating…';
      generateStatus.className = 'ai-hint loading';
      const userMessage = buildUserMessage(loadedTicket, notes);
      const result = await callCopilot(SYSTEM_PROMPT, userMessage);
      outputEl.textContent = result;
      outputWrap.classList.add('visible');
      createWrap.classList.add('visible');
      createStatus.textContent = '';
      generateStatus.textContent = '';
    } catch (err) {
      generateStatus.textContent = err.message;
      generateStatus.className = 'ai-hint error';
    } finally {
      generateBtn.disabled = false;
    }
  });

  root.getElementById('ai-copy-output').addEventListener('click', () => {
    const btn = root.getElementById('ai-copy-output');
    navigator.clipboard.writeText(outputEl.textContent).then(() => {
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = 'Copy'; }, 2000);
    }).catch(() => {
      btn.textContent = 'Copy failed';
      setTimeout(() => { btn.textContent = 'Copy'; }, 2000);
    });
  });

  root.getElementById('ai-create-ticket').addEventListener('click', async () => {
    if (!loadedTicket) return;
    const outputText = outputEl.textContent.trim();
    if (!outputText || !parseSummary(outputText)) {
      createStatus.textContent = 'Could not parse Summary from output.';
      createStatus.className = 'ai-hint error';
      return;
    }
    const createBtn = root.getElementById('ai-create-ticket');
    createBtn.disabled = true;
    createStatus.textContent = 'Resolving team…';
    createStatus.className = 'ai-hint loading';
    try {
      const fields = await buildTicketFields(loadedTicket.key, selectedTeam, outputText);
      createStatus.textContent = 'Creating…';
      createStatus.className = 'ai-hint loading';
      const { key: newKey } = await createIssue(fields);
      await linkIssues(newKey, loadedTicket.key);
      createStatus.innerHTML = '';
      createStatus.appendChild(document.createTextNode('✓ Created '));
      const link = document.createElement('a');
      link.href = `${JIRA_BROWSE_BASE}/${newKey}`;
      link.target = '_blank';
      link.rel = 'noopener';
      link.style.cssText = 'color:#059669;font-weight:600';
      link.textContent = newKey;
      createStatus.appendChild(link);
      createStatus.className = 'ai-hint';
    } catch (err) {
      createStatus.textContent = err.message;
      createStatus.className = 'ai-hint error';
    } finally {
      createBtn.disabled = false;
    }
  });

  function activate() {
    panel.style.display = 'flex';
    if (getParentKey) {
      const key = getParentKey();
      if (key && !ticketInput.value) ticketInput.value = key;
    }
  }

  function deactivate() {
    panel.style.display = 'none';
  }

  return { activate, deactivate };
}
